import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Phone, Mail, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { MakeOfferDialog } from "@/components/properties/MakeOfferDialog";

interface SellerInfo {
  id: string;
  full_name: string | null;
  business_name: string | null;
  phone_number: string | null;
  email: string | null;
  profile_image_url: string | null;
}

interface PropertySellerInfoProps {
  sellerInfo: SellerInfo | null;
  propertyId?: string;
  propertyAddress?: string;
  listingPrice?: number;
  isAuthenticated?: boolean;
  showViewAllListings?: boolean;
}

export const PropertySellerInfo = ({
  sellerInfo,
  propertyId,
  propertyAddress,
  listingPrice,
  isAuthenticated = false,
  showViewAllListings = false
}: PropertySellerInfoProps) => {
  const hasContactInfo = sellerInfo?.email || sellerInfo?.phone_number;
  const showOfferButton = propertyId && propertyAddress && listingPrice && sellerInfo?.id;
  
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4">Listing Agent</h3>
        <div className="flex items-center space-x-4">
          <Avatar className={showViewAllListings && sellerInfo?.id ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}>
            <AvatarImage src={sellerInfo?.profile_image_url || ''} />
            <AvatarFallback className="bg-godyspo-blue text-white">
              {sellerInfo?.full_name ? sellerInfo.full_name.split(' ').map(n => n[0]).join('') : 'NA'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            {showViewAllListings && sellerInfo?.id ? (
              <Link 
                to={`/buyer/sellers/${sellerInfo.id}/listings`}
                className="group"
              >
                <p className="font-medium group-hover:text-primary group-hover:underline transition-all">
                  {sellerInfo?.full_name || 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">{sellerInfo?.business_name || 'N/A'}</p>
                <p className="text-xs text-primary mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  View all listings →
                </p>
              </Link>
            ) : (
              <>
                <p className="font-medium">{sellerInfo?.full_name || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">{sellerInfo?.business_name || 'N/A'}</p>
              </>
            )}
          </div>
        </div>
        <Separator className="my-4" />
        
        {hasContactInfo ? (
          <>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="text-muted-foreground mr-1">Phone:</span>
                {sellerInfo?.phone_number || 'N/A'}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground mr-1">Email:</span>
                {sellerInfo?.email || 'N/A'}
              </p>
            </div>
            
            <Separator className="my-4" />
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Contact Seller</h4>
              <div className="flex gap-2">
                {sellerInfo?.phone_number && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    asChild
                    className="flex-1"
                  >
                    <a href={`tel:${sellerInfo.phone_number}`}>
                      <Phone className="h-3 w-3 mr-1" />
                      <span className="text-xs">Call</span>
                    </a>
                  </Button>
                )}
                
                {sellerInfo?.email && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    asChild
                    className="flex-1"
                  >
                    <a href={`mailto:${sellerInfo.email}`}>
                      <Mail className="h-3 w-3 mr-1" />
                      <span className="text-xs">Email</span>
                    </a>
                  </Button>
                )}
              </div>
              
              {showOfferButton && (
                <div data-tour="make-offer">
                <MakeOfferDialog
                  propertyId={propertyId}
                  sellerId={sellerInfo.id}
                  propertyAddress={propertyAddress}
                  listingPrice={listingPrice}
                  isAuthenticated={isAuthenticated}
                />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">
              Sign in to view contact details and connect with the seller
            </p>
            <Button asChild size="sm" className="w-full">
              <Link to="/buyer/login">
                <LogIn className="h-4 w-4 mr-2" />
                Sign In to Contact
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
